import { useMemo, useState } from 'react';

import { usePortfolio } from '../contexts/PortfolioContext';
import { colors } from '../core/theme/tokens';
import type { SyncedPortfolioSnapshot } from '../services/portfolioSyncService';

export type WealthTrendTab = {
  id: string;
  name: string;
  color: string;
};

export type WealthTrendPoint = {
  asOfDate: string;
  balance: number;
};

export type WealthAccountDelta = {
  accountId: string;
  label: string;
  type: string;
  currentBalance: number;
  previousBalance: number | null;
  delta: number | null;
};

const TYPE_COLORS = [
  colors.accent,
  '#60A5FA',
  '#A78BFA',
  '#F472B6',
  colors.warning,
  '#34D399',
  '#FB923C',
  '#38BDF8',
];

function colorForIndex(index: number): string {
  return TYPE_COLORS[index % TYPE_COLORS.length]!;
}

/** Prefer stable accountId; fall back to label for older rows. */
function accountKey(account: {
  accountId?: string;
  label: string;
}): string {
  const id = account.accountId?.trim();
  if (id) return `id:${id}`;
  return `label:${account.label.trim().toLowerCase()}`;
}

function balanceForTab(snapshot: SyncedPortfolioSnapshot, tabId: string): number {
  if (tabId === 'all') return snapshot.totalBalance;
  return snapshot.accounts
    .filter((a) => accountKey(a) === tabId)
    .reduce((sum, a) => sum + a.balance, 0);
}

function previousBalanceForKey(
  previous: SyncedPortfolioSnapshot | null,
  key: string,
): number | null {
  if (!previous) return null;
  const match = previous.accounts.find((a) => accountKey(a) === key);
  return match?.balance ?? null;
}

export type WealthAccountTrendsResult = {
  tabs: WealthTrendTab[];
  selectedTabId: string;
  setSelectedTabId: (id: string) => void;
  points: WealthTrendPoint[];
  accountDeltas: (focusedAsOfDate: string | null) => WealthAccountDelta[];
  hasHistory: boolean;
};

/**
 * Trends from Mongo snapshots — one bar per upload / update day.
 * Tabs and deltas track stable accountId so manual edits trend over months.
 */
export function useWealthAccountTrends(options?: {
  limit?: number;
}): WealthAccountTrendsResult {
  const limit = options?.limit ?? 8;
  const { snapshots } = usePortfolio();
  const [selectedTabId, setSelectedTabId] = useState('all');

  const ordered = useMemo(
    () =>
      [...snapshots]
        .sort((a, b) => a.asOfDate.localeCompare(b.asOfDate))
        .slice(-limit),
    [snapshots, limit],
  );

  const tabs = useMemo((): WealthTrendTab[] => {
    const seen = new Map<string, string>();
    for (const snap of ordered) {
      for (const account of snap.accounts) {
        const key = accountKey(account);
        const label = account.label.trim();
        if (label && !seen.has(key)) seen.set(key, label);
      }
    }
    return [...seen.entries()].map(([id, name], index) => ({
      id,
      name,
      color: colorForIndex(index),
    }));
  }, [ordered]);

  const activeTabId =
    selectedTabId === 'all' || tabs.some((t) => t.id === selectedTabId)
      ? selectedTabId
      : 'all';

  const points = useMemo((): WealthTrendPoint[] => {
    return ordered.map((snap) => ({
      asOfDate: snap.asOfDate,
      balance: balanceForTab(snap, activeTabId),
    }));
  }, [ordered, activeTabId]);

  const accountDeltas = useMemo(() => {
    return (focusedAsOfDate: string | null): WealthAccountDelta[] => {
      if (!focusedAsOfDate) return [];
      const focusIndex = ordered.findIndex((s) => s.asOfDate === focusedAsOfDate);
      if (focusIndex < 0) return [];
      const current = ordered[focusIndex]!;
      const previous = focusIndex > 0 ? ordered[focusIndex - 1]! : null;

      return current.accounts
        .map((account) => {
          const key = accountKey(account);
          if (activeTabId !== 'all' && key !== activeTabId) return null;
          const prevBal = previousBalanceForKey(previous, key);
          return {
            accountId: account.accountId?.trim() || key,
            label: account.label,
            type: 'investment',
            currentBalance: account.balance,
            previousBalance: prevBal,
            delta: prevBal != null ? account.balance - prevBal : null,
          };
        })
        .filter((row): row is WealthAccountDelta => row != null);
    };
  }, [ordered, activeTabId]);

  return {
    tabs,
    selectedTabId: activeTabId,
    setSelectedTabId,
    points,
    accountDeltas,
    hasHistory: points.length > 0,
  };
}
